import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { ApolloProvider, useApolloClient } from '@apollo/client';
import { identifyUser, resetUser } from '@/lib/analytics';
import { createApolloClient } from '@go2fix/shared';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ensurePushSubscription } from '@/services/PushService';
import { CompanyProvider } from '@/context/CompanyContext';
import { PlatformProvider, usePlatform } from '@/context/PlatformContext';
import { LanguageProvider, useLanguage } from '@/context/LanguageContext';
import { PageAlternateProvider } from '@/context/PageAlternateContext';
import { ROUTE_MAP } from '@/i18n/routes';
import ErrorBoundary from '@/components/ErrorBoundary';
import PhoneGate from '@/components/PhoneGate';
import CookieConsent from '@/components/CookieConsent';
import ChatwootRouteGate from '@/components/ChatwootRouteGate';
import RouteTransition from '@/components/RouteTransition';
import InstallPrompt from '@/components/pwa/InstallPrompt';
import OfflineBanner from '@/components/pwa/OfflineBanner';
import UpdateAvailableBanner from '@/components/pwa/UpdateAvailableBanner';
import OnboardingGate from '@/features/onboarding/OnboardingGate';

// Layouts (eager — needed immediately for all routes)
import PublicLayout from '@/components/layout/PublicLayout';
import BookingLayout from '@/components/layout/BookingLayout';
import ClientLayout from '@/components/layout/ClientLayout';
import CompanyLayout from '@/components/layout/CompanyLayout';
import WorkerLayout from '@/components/layout/WorkerLayout';
import AdminLayout from '@/components/layout/AdminLayout';

// ─── Lazy page imports ────────────────────────────────────────────────────────

// Public pages
const HomePage = lazy(() => import('@/pages/HomePage'));
const BookingPage = lazy(() => import('@/pages/BookingPage'));
const KernelBookingPage = lazy(() => import('@/pages/KernelBookingPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const RegisterCompanyPage = lazy(() => import('@/pages/RegisterCompanyPage'));
const ClaimCompanyPage = lazy(() => import('@/pages/ClaimCompanyPage'));
const WaitlistPage = lazy(() => import('@/pages/WaitlistPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const ForCompaniesPage = lazy(() => import('@/pages/ForCompaniesPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const GdprPage = lazy(() => import('@/pages/GdprPage'));
const BlogListPage = lazy(() => import('@/pages/blog/BlogListPage'));
const BlogPostPage = lazy(() => import('@/pages/blog/BlogPostPage'));
const CategoryLandingPage = lazy(() => import('@/pages/CategoryLandingPage'));
const CityServicePage = lazy(() => import('@/pages/CityServicePage'));
const VsHomerunPage = lazy(() => import('@/pages/VsHomerunPage'));
const VsNecesitPage = lazy(() => import('@/pages/VsNecesitPage'));

// Payment pages
const PayCustomRequestPage = lazy(() => import('@/pages/PayCustomRequestPage'));

// Client pages
const ClientDashboardPage = lazy(() => import('@/pages/client/ClientDashboardPage'));
const MyBookingsPage = lazy(() => import('@/pages/client/MyBookingsPage'));
const ClientBookingDetailPage = lazy(() => import('@/pages/client/BookingDetailPage'));
const ChatPage = lazy(() => import('@/pages/client/ChatPage'));
const ProfilePage = lazy(() => import('@/pages/client/ProfilePage'));
const AddressesPage = lazy(() => import('@/pages/client/AddressesPage'));
const PaymentMethodsPage = lazy(() => import('@/pages/client/PaymentMethodsPage'));
const PaymentHistoryPage = lazy(() => import('@/pages/client/PaymentHistoryPage'));
const ClientInvoicesPage = lazy(() => import('@/pages/client/InvoicesPage'));
const RecurringGroupDetailPage = lazy(() => import('@/pages/client/RecurringGroupDetailPage'));
const ClientSubscriptionsPage = lazy(() => import('@/pages/client/SubscriptionsPage'));
const SubscriptionDetailPage = lazy(() => import('@/pages/client/SubscriptionDetailPage'));
const SupportPage = lazy(() => import('@/pages/client/SupportPage'));

// Company pages
const CompanyDashboardPage = lazy(() => import('@/pages/company/DashboardPage'));
const DocumentUploadPage = lazy(() => import('@/pages/company/DocumentUploadPage'));
const CompanyOrdersPage = lazy(() => import('@/pages/company/OrdersPage'));
const CompanyOrderDetailPage = lazy(() => import('@/pages/company/OrderDetailPage'));
const TeamPage = lazy(() => import('@/pages/company/TeamPage'));
const WorkerDetailPage = lazy(() => import('@/pages/company/WorkerDetailPage'));
const CompanySettingsPage = lazy(() => import('@/pages/company/SettingsPage'));
const CompanyMessagesPage = lazy(() => import('@/pages/company/MessagesPage'));
const CompanyCalendarPage = lazy(() => import('@/pages/company/CalendarPage'));
const CompanyPayoutsPage = lazy(() => import('@/pages/company/PayoutsPage'));
const CompanyInvoicesPage = lazy(() => import('@/pages/company/CompanyInvoicesPage'));
const CompanySubscriptionsPage = lazy(() => import('@/pages/company/SubscriptionsPage'));
const CompanySubscriptionDetailPage = lazy(() => import('@/pages/company/SubscriptionDetailPage'));
const CompanyReviewsPage = lazy(() => import('@/pages/company/ReviewsPage'));

// Worker pages
const AcceptInvitePage = lazy(() => import('@/pages/worker/AcceptInvitePage'));
const WorkerDashboardPage = lazy(() => import('@/pages/worker/DashboardPage'));
const WorkerOrdersPage = lazy(() => import('@/pages/worker/OrdersPage'));
const WorkerSchedulePage = lazy(() => import('@/pages/worker/SchedulePage'));
const WorkerJobDetailPage = lazy(() => import('@/pages/worker/JobDetailPage'));
const WorkerSettingsPage = lazy(() => import('@/pages/worker/SettingsPage'));
const PersonalityTestPage = lazy(() => import('@/pages/worker/PersonalityTestPage'));
const WorkerDocumentUploadPage = lazy(() => import('@/pages/worker/DocumentUploadPage'));
const WorkerEarningsPage = lazy(() => import('@/pages/worker/EarningsPage'));

// Admin pages
const AdminDashboardPage = lazy(() => import('@/pages/admin/DashboardPage'));
const CompaniesPage = lazy(() => import('@/pages/admin/CompaniesPage'));
const CompanyDetailPage = lazy(() => import('@/pages/admin/CompanyDetailPage'));
const AdminBookingsPage = lazy(() => import('@/pages/admin/BookingsPage'));
const AdminBookingDetailPage = lazy(() => import('@/pages/admin/BookingDetailPage'));
const UsersPage = lazy(() => import('@/pages/admin/UsersPage'));
const UserDetailPage = lazy(() => import('@/pages/admin/UserDetailPage'));
const AdminSettingsPage = lazy(() => import('@/pages/admin/SettingsPage'));
const ReportsPage = lazy(() => import('@/pages/admin/ReportsPage'));
const ReviewsPage = lazy(() => import('@/pages/admin/ReviewsPage'));
const AdminPaymentsPage = lazy(() => import('@/pages/admin/PaymentsPage'));
const AdminInvoicesPage = lazy(() => import('@/pages/admin/AdminInvoicesPage'));
const AdminSubscriptionsPage = lazy(() => import('@/pages/admin/SubscriptionsPage'));
const AdminSubscriptionDetailPage = lazy(() => import('@/pages/admin/AdminSubscriptionDetailPage'));
const AdminPayoutsPage = lazy(() => import('@/pages/admin/AdminPayoutsPage'));
const AdminCompanyStandingPage = lazy(() => import('@/pages/admin/AdminCompanyStandingPage'));
const AdminHealthPage = lazy(() => import('@/pages/admin/AdminHealthPage'));
const AdminRefundsPage = lazy(() => import('@/pages/admin/RefundsPage'));
const PromoCodesPage = lazy(() => import('@/pages/admin/PromoCodesPage'));
const DisputesPage = lazy(() => import('@/pages/admin/DisputesPage'));
const WaitlistLeadsPage = lazy(() => import('@/pages/admin/WaitlistLeadsPage'));
const ReviewQueuePage = lazy(() => import('@/pages/admin/ReviewQueuePage'));
const DocsPage = lazy(() => import('@/pages/admin/DocsPage'));
const AdminBookingDocumentsPage = lazy(() => import('@/pages/admin/AdminBookingDocumentsPage'));
const KernelServicePreviewPage = lazy(() => import('@/pages/admin/KernelServicePreviewPage'));
const KernelServicesPage = lazy(() => import('@/pages/admin/KernelServicesPage'));

// ─── Apollo Client ───────────────────────────────────────────────────────────

const httpEndpoint =
  import.meta.env.VITE_GRAPHQL_ENDPOINT || 'http://localhost:8080/query';
const wsEndpoint = httpEndpoint.replace(/^http/, 'ws');

const client = createApolloClient(httpEndpoint, wsEndpoint);

// ─── Page Loader ─────────────────────────────────────────────────────────────

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

// ─── Platform Gate ───────────────────────────────────────────────────────────

function BookingGateRoute() {
  const { isPreRelease, loading } = usePlatform();
  const { lang } = useLanguage();
  if (loading) return null;
  if (isPreRelease) return <Navigate to={ROUTE_MAP.waitlist[lang]} replace />;
  return <BookingPage />;
}

// ─── Route Guards ────────────────────────────────────────────────────────────

const ROLE_HOME: Record<string, string> = {
  CLIENT: '/cont',
  COMPANY_ADMIN: '/firma',
  WORKER: '/worker',
  GLOBAL_ADMIN: '/admin',
};

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/autentificare" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
}

function RoleRoute({ children, role }: { children: React.ReactNode; role: string }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user || user.role !== role) {
    const home = user ? (ROLE_HOME[user.role] || '/') : '/autentificare';
    return <Navigate to={home} replace />;
  }

  return <>{children}</>;
}

// ─── Analytics User Sync ─────────────────────────────────────────────────────

function AnalyticsSync() {
  const { user } = useAuth();
  useEffect(() => {
    if (user) {
      identifyUser(user.id, { email: user.email, role: user.role, name: user.fullName });
    } else {
      resetUser();
    }
  }, [user]);
  return null;
}

// ─── Push Subscription Sync ──────────────────────────────────────────────────

// Re-assert the push subscription whenever a user is signed in.
//
// Granting permission once is not enough to stay reachable. A browser can drop
// a subscription at any time, and the backend deletes an endpoint after
// repeated delivery failures. Neither produces any signal in the app — the
// worker simply stops being told about jobs and has no way to know.
//
// subscribeToPush is idempotent on both sides (pushManager returns the existing
// subscription, and registerPushSubscription upserts on endpoint), so running
// it on every sign-in is cheap and repairs both states.
//
// It never prompts: it returns early unless permission is already 'granted'.
// The prompt belongs in the onboarding slide, behind a user gesture.
function PushSync() {
  const { user } = useAuth();
  const client = useApolloClient();
  useEffect(() => {
    if (!user) return;
    void ensurePushSubscription(client);
  }, [user, client]);
  return null;
}

// ─── Routes ──────────────────────────────────────────────────────────────────

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <RouteTransition>
      <Routes>
        {/* Standalone — no Header/Footer (split-screen layout) */}
        <Route path="/autentificare" element={<LoginPage />} />
        <Route path="/inregistrare-firma" element={<RegisterCompanyPage />} />
        <Route path="/invitare" element={<AcceptInvitePage />} />
        <Route path="/plata/extra/:token" element={<PayCustomRequestPage />} />

        {/* ── Booking flow (Header only, no Footer) ── */}
        <Route element={<BookingLayout />}>
          <Route path="/rezervare" element={<BookingGateRoute />} />
          <Route path="/en/booking" element={<BookingGateRoute />} />
          {/* Kernel-driven booking — service per :slug. New shape, used while
              the legacy /rezervare flow is being retired. */}
          <Route path="/rezervare/:slug" element={<KernelBookingPage />} />
          <Route path="/en/booking/:slug" element={<KernelBookingPage />} />
        </Route>

        {/* ── Romanian public routes (no prefix) ── */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/servicii" element={<Navigate to="/#servicii" replace />} />
          <Route path="/servicii/:categorySlug" element={<CategoryLandingPage />} />
          {/* Programmatic city × service landings — see src/data/programmaticPages.ts */}
          <Route path="/servicii/:slug/:city" element={<CityServicePage />} />
          <Route path="/claim-firma/:token" element={<ClaimCompanyPage />} />
          <Route path="/lista-asteptare" element={<WaitlistPage />} />
          <Route path="/despre-noi" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/pentru-firme" element={<ForCompaniesPage />} />
          <Route path="/termeni" element={<TermsPage />} />
          <Route path="/confidentialitate" element={<PrivacyPage />} />
          <Route path="/gdpr" element={<GdprPage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/vs/homerun" element={<VsHomerunPage />} />
          <Route path="/vs/necesit" element={<VsNecesitPage />} />
        </Route>

        {/* ── English public routes (/en/ prefix) ── */}
        <Route path="/en" element={<PublicLayout />}>
          <Route index element={<HomePage />} />
          <Route path="services/:categorySlug" element={<CategoryLandingPage />} />
          <Route path="services/:slug/:city" element={<CityServicePage />} />
          <Route path="about-us" element={<AboutPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="for-companies" element={<ForCompaniesPage />} />
          <Route path="waitlist" element={<WaitlistPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="gdpr" element={<GdprPage />} />
          <Route path="blog" element={<BlogListPage />} />
          <Route path="blog/:slug" element={<BlogPostPage />} />
          <Route path="vs/homerun" element={<VsHomerunPage />} />
          <Route path="vs/necesit" element={<VsNecesitPage />} />
        </Route>

        {/* Client routes - Sidebar layout, auth + CLIENT role */}
        <Route
          path="/cont"
          element={
            <ProtectedRoute>
              <RoleRoute role="CLIENT">
                <PhoneGate>
                  <ClientLayout />
                </PhoneGate>
              </RoleRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<ClientDashboardPage />} />
          <Route path="comenzi" element={<MyBookingsPage />} />
          <Route path="comenzi/:id" element={<ClientBookingDetailPage />} />
          <Route path="recurente/:id" element={<RecurringGroupDetailPage />} />
          <Route path="abonamente" element={<ClientSubscriptionsPage />} />
          <Route path="abonamente/:id" element={<SubscriptionDetailPage />} />
          <Route path="mesaje" element={<ChatPage />} />
          <Route path="adrese" element={<AddressesPage />} />
          <Route path="plati" element={<PaymentMethodsPage />} />
          <Route path="plati/istoric" element={<PaymentHistoryPage />} />
          <Route path="facturi" element={<ClientInvoicesPage />} />
          <Route path="setari" element={<ProfilePage />} />
          <Route path="ajutor" element={<SupportPage />} />
        </Route>

        {/* Company routes - Sidebar layout, auth + COMPANY_ADMIN role */}
        <Route
          path="/firma"
          element={
            <ProtectedRoute>
              <RoleRoute role="COMPANY_ADMIN">
                <PhoneGate>
                  <CompanyProvider>
                    <CompanyLayout />
                  </CompanyProvider>
                </PhoneGate>
              </RoleRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<CompanyDashboardPage />} />
          <Route path="documente-obligatorii" element={<DocumentUploadPage />} />
          <Route path="comenzi" element={<CompanyOrdersPage />} />
          <Route path="comenzi/:id" element={<CompanyOrderDetailPage />} />
          <Route path="program" element={<CompanyCalendarPage />} />
          <Route path="mesaje" element={<CompanyMessagesPage />} />
          <Route path="echipa" element={<TeamPage />} />
          <Route path="echipa/:id" element={<WorkerDetailPage />} />
          <Route path="recenzii" element={<CompanyReviewsPage />} />
          <Route path="plati" element={<CompanyPayoutsPage />} />
          <Route path="abonamente" element={<CompanySubscriptionsPage />} />
          <Route path="abonamente/:id" element={<CompanySubscriptionDetailPage />} />
          <Route path="facturi" element={<CompanyInvoicesPage />} />
          <Route path="setari" element={<CompanySettingsPage />} />
        </Route>

        {/* Worker routes - Sidebar layout, auth + WORKER role */}
        <Route
          path="/worker"
          element={
            <ProtectedRoute>
              <RoleRoute role="WORKER">
                <PhoneGate>
                  <WorkerLayout />
                </PhoneGate>
              </RoleRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<WorkerDashboardPage />} />
          <Route path="test-personalitate" element={<PersonalityTestPage />} />
          <Route path="documente-obligatorii" element={<WorkerDocumentUploadPage />} />
          <Route path="comenzi" element={<WorkerOrdersPage />} />
          <Route path="comenzi/:id" element={<WorkerJobDetailPage />} />
          <Route path="program" element={<WorkerSchedulePage />} />
          <Route path="castiguri" element={<WorkerEarningsPage />} />
          <Route path="mesaje" element={<ChatPage />} />
          <Route path="profil" element={<WorkerSettingsPage />} />
        </Route>

        {/* Admin routes - Sidebar layout, auth + GLOBAL_ADMIN role */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <RoleRoute role="GLOBAL_ADMIN">
                <AdminLayout />
              </RoleRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboardPage />} />
          <Route path="companii" element={<CompaniesPage />} />
          <Route path="companii/:id" element={<CompanyDetailPage />} />
          <Route path="comenzi" element={<AdminBookingsPage />} />
          <Route path="comenzi/:id" element={<AdminBookingDetailPage />} />
          <Route path="abonamente" element={<AdminSubscriptionsPage />} />
          <Route path="abonamente/:id" element={<AdminSubscriptionDetailPage />} />
          <Route path="utilizatori" element={<UsersPage />} />
          <Route path="utilizatori/:id" element={<UserDetailPage />} />
          <Route path="plati" element={<AdminPaymentsPage />} />
          <Route path="viramente" element={<AdminPayoutsPage />} />
          <Route path="solduri" element={<AdminCompanyStandingPage />} />
          <Route path="stare-sistem" element={<AdminHealthPage />} />
          <Route path="rambursari" element={<AdminRefundsPage />} />
          <Route path="facturi" element={<AdminInvoicesPage />} />
          <Route path="rapoarte" element={<ReportsPage />} />
          <Route path="recenzii" element={<ReviewsPage />} />
          <Route path="promo-coduri" element={<PromoCodesPage />} />
          <Route path="dispute" element={<DisputesPage />} />
          <Route path="aprobari" element={<ReviewQueuePage />} />
          <Route path="lista-asteptare" element={<WaitlistLeadsPage />} />
          <Route path="documente-rezervari" element={<AdminBookingDocumentsPage />} />
          <Route path="setari" element={<Navigate to="/admin/setari/platforma" replace />} />
          <Route path="setari/platforma" element={<AdminSettingsPage />} />
          <Route path="setari/servicii" element={<AdminSettingsPage />} />
          <Route path="setari/orase" element={<AdminSettingsPage />} />
          <Route path="setari/audit" element={<AdminSettingsPage />} />
          {/* Kernel service preview — exercises form_schema + pricing engine end-to-end */}
          {/* Kernel services CRUD — author / edit service definitions live */}
          <Route path="setari/servicii/kernel" element={<KernelServicesPage />} />
          <Route path="setari/servicii/preview" element={<KernelServicePreviewPage />} />
          <Route path="setari/servicii/preview/:slug" element={<KernelServicePreviewPage />} />
          <Route path="docs" element={<DocsPage />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<PublicLayout />}>
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
      </RouteTransition>
    </Suspense>
  );
}

// ─── App ─────────────────────────────────────────────────────────────────────

function App() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      localStorage.setItem('pendingReferralCode', ref.toUpperCase());
    }
  }, []);

  return (
    <ApolloProvider client={client}>
      {/* AuthProvider must sit outside PlatformProvider: PlatformProvider skips
          the admin-only platformSettings query for anonymous visitors, which
          means it needs to read auth state. */}
      <AuthProvider>
        <PlatformProvider>
          <AnalyticsSync />
          <PushSync />
          <PageAlternateProvider>
            <LanguageProvider>
              <ErrorBoundary>
                <OfflineBanner />
                <UpdateAvailableBanner />
                <AppRoutes />
                <ChatwootRouteGate />
                <InstallPrompt />
                <OnboardingGate />
                <CookieConsent />
              </ErrorBoundary>
            </LanguageProvider>
          </PageAlternateProvider>
        </PlatformProvider>
      </AuthProvider>
    </ApolloProvider>
  );
}

export default App;
